import { forwardRef, type ReactNode, useRef } from 'react';

import { CategoricalBarChartCategoryAxis } from './components/axes/CategoricalBarChartCategoryAxis.js';
import { CategoricalBarChartValueAxis } from './components/axes/CategoricalBarChartValueAxis.js';
import { CategoricalBarChartOverlays } from './components/interactions/CategoricalBarChartOverlays.js';
import { CategoricalThresholdArea } from './components/thresholds/CategoricalThresholdArea.js';
import { CategoricalThresholdIndicators } from './components/thresholds/CategoricalThresholdIndicators.js';
import { CATEGORICAL_BAR_CHART_ARIA_LABEL, DISPLAY_NAME } from './constants.js';
import { useCategoricalBarChartBaseConfig } from './hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartCategoryAxisConfig } from './hooks/useCategoricalBarChartCategoryAxisConfig.js';
import { useCategoricalBarChartData } from './hooks/useCategoricalBarChartData.js';
import { useCategoricalBarChartLegend } from './hooks/useCategoricalBarChartLegend.js';
import { CategoricalBarChartPlot } from './layout/CategoricalBarChartPlot.js';
import { CategoricalBarChartFormatterProvider } from './providers/CategoricalBarChartFormatterProvider.js';
import { CategoricalBarScalesProvider } from './providers/CategoricalBarScalesProviders.js';
import { AREAS } from './types/categorical-bar-area-rules.js';
import { isVertical } from './utils/layout.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { ChartGrid } from '../core/components/chart-grid/ChartGridRoot.js';
import { useChartLayoutLoading } from '../core/components/chart-layout/hooks/useChartLayoutLoading.js';
import { useChartLayoutOrientation } from '../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { useGraphSize } from '../core/components/chart-layout/hooks/useGraphSize.js';
import {
  BOTTOM_PADDING_ADJUSTMENT,
  OVERLAY_PADDING,
} from '../core/components/data-point/constants.js';

const categoricalGridTemplateAreas = `
  "${AREAS.leftAxis} ${AREAS.leftThreshold} plot"
  ". . ${AREAS.bottomThreshold}"
  ". . ${AREAS.bottomAxis}"
`;

const categoricalGridTemplateColumns = (
  leftAxisMaxWidth: number | 'auto' = 'auto',
) => {
  const max =
    typeof leftAxisMaxWidth === 'number'
      ? `${leftAxisMaxWidth}px`
      : leftAxisMaxWidth;

  return `minmax(0, ${max}) auto minmax(60%, 1fr)`;
};

const categoricalGridTemplateRows = (
  bottomAxisMaxHeight: number | 'auto' = 'auto',
) => {
  const max =
    typeof bottomAxisMaxHeight === 'number'
      ? `${bottomAxisMaxHeight}px`
      : bottomAxisMaxHeight;

  return `minmax(60%, 1fr) auto minmax(0, ${max})`;
};

export const CategoricalBarChart = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  { children?: ReactNode }
>((props, forwardedRef) => {
  const loading = useChartLayoutLoading();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartAndForwardedRef = useMergeRefs([forwardedRef, chartRef]);
  const { maxSize } = useCategoricalBarChartCategoryAxisConfig();
  const { layout } = useCategoricalBarChartBaseConfig();
  const { hidden: legendHidden } = useCategoricalBarChartLegend();
  const { orientation } = useChartLayoutOrientation();
  const { uniqueDimensions } = useCategoricalBarChartData();

  const maxSizeLeftAxis = isVertical(layout) ? undefined : maxSize;
  const maxSizeBottomAxis = isVertical(layout) ? maxSize : undefined;

  const { width, height } = useGraphSize();

  //TODO (APPDEV-9905): Improve this logic to make Legend Hidden / Unique dimensions logic to be part of one same context, but not for this bugfix.
  const dontShowLegend = legendHidden || uniqueDimensions.length <= 1;
  const noLegendRight = dontShowLegend || orientation === 'vertical';
  const noLegendBottom = dontShowLegend || orientation === 'horizontal';

  const chartGridHeight =
    height - (noLegendBottom ? OVERLAY_PADDING + BOTTOM_PADDING_ADJUSTMENT : 0);
  return (
    <div
      style={{ paddingTop: OVERLAY_PADDING }}
      className={defaultChartSize}
      aria-label={CATEGORICAL_BAR_CHART_ARIA_LABEL}
      role="img"
    >
      <ChartGrid
        width={width - (noLegendRight ? OVERLAY_PADDING : 0)}
        height={chartGridHeight}
        gridTemplateAreas={categoricalGridTemplateAreas}
        gridTemplateColumns={categoricalGridTemplateColumns(maxSizeLeftAxis)}
        gridTemplateRows={categoricalGridTemplateRows(maxSizeBottomAxis)}
        ref={chartAndForwardedRef}
      >
        <CategoricalBarScalesProvider>
          <CategoricalBarChartFormatterProvider>
            <CategoricalBarChartCategoryAxis />
            <CategoricalBarChartValueAxis />

            <ChartGrid.PlotArea>
              <CategoricalBarChartPlot />

              <CategoricalThresholdIndicators />

              {loading ? null : <CategoricalBarChartOverlays />}
            </ChartGrid.PlotArea>
            <CategoricalThresholdArea />
          </CategoricalBarChartFormatterProvider>
        </CategoricalBarScalesProvider>
      </ChartGrid>
    </div>
  );
});

(
  CategoricalBarChart as typeof CategoricalBarChart & { displayName: string }
).displayName = DISPLAY_NAME;
